import Vue from 'vue'
import App from './App.vue'
import router from './_router'
import store from './_store'
import vuetify from './_plugins/vuetify'
import '@/_styles/main.scss'
import axios from 'axios'
import jwt_decode from 'jwt-decode'
import moment from 'moment'
import mrStaffingAppConfig from '@/apps/mrstaffing/app.config'
import draggable from "vuedraggable";
import { Container, Draggable } from "vue-smooth-dnd";
import VueClipboard from 'vue-clipboard2'
import i18n from '@/_plugins/i18n'

Vue.config.productionTip = false
Vue.component('container',Container);
Vue.component('draggable',Draggable);
Vue.use(VueClipboard);

const credentialCacheKey = 'credential-cache';
const clientId = mrStaffingAppConfig.settings.clientId;
let refreshRequest = null;

function getCredentialCache() {
  try {
    return JSON.parse(window.localStorage.getItem(credentialCacheKey)) || {};
  } catch (err) {
    return {};
  }
}

function setCredentialCache(cache) {
  window.localStorage.setItem(credentialCacheKey, JSON.stringify(cache));
}

function getCachedCredentials() {
  return getCredentialCache()[clientId] || null;
}

function clearCachedCredentials() {
  const cache = getCredentialCache();
  delete cache[clientId];
  setCredentialCache(cache);
}

function storeRefreshedCredentials(credentials, tokenResponse) {
  const cache = getCredentialCache();
  const expiresOn = moment().add(tokenResponse.expires_in, 'seconds').toISOString();

  cache[clientId] = {
    ...credentials,
    ...tokenResponse,
    expires_on: expiresOn,
    $expires_on: expiresOn,
    time_stamp: moment().toISOString(),
    'jwt-decode': jwt_decode(tokenResponse.access_token),
  };

  setCredentialCache(cache);
  return cache[clientId];
}

async function refreshCachedToken(credentials) {
  const formData = new FormData();
  formData.append('client_id', credentials.client_id || clientId);
  formData.append('scope', credentials.scope || mrStaffingAppConfig.settings.scope);
  formData.append('refresh_token', credentials.refresh_token);
  formData.append('grant_type', 'refresh_token');

  const response = await axios({
    method: 'post',
    url: credentials.token_uri || mrStaffingAppConfig.settings.tokenUrl,
    data: formData,
  });

  return storeRefreshedCredentials(credentials, response.data);
}

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const statusCode = error.response ? error.response.status : null;

    if (!originalRequest || originalRequest._retry || statusCode !== 401) {
      return Promise.reject(error);
    }

    const credentials = getCachedCredentials();
    if (!credentials || !credentials.refresh_token) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      refreshRequest = refreshRequest || refreshCachedToken(credentials);
      const refreshedCredentials = await refreshRequest;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `bearer ${refreshedCredentials.access_token}`;
      return axios(originalRequest);
    } catch (refreshError) {
      clearCachedCredentials();
      return Promise.reject(refreshError);
    } finally {
      refreshRequest = null;
    }
  }
);

new Vue({
  router,
  store,
  vuetify,
  i18n,
  render: h => h(App)
}).$mount('#app')
